import { useCallback, useRef } from 'react';
import { fieldChecked } from './polaris';
import './RowCheckbox.css';

type Props = {
    id?: string;
    accessibilityLabel: string;
    checked: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
};

/**
 * Selection checkbox for an <s-table-row> that has a clickDelegate. The padded
 * area around the checkbox toggles it, so a near miss does not open the row.
 */
export default function RowCheckbox({ id, accessibilityLabel, checked, indeterminate, disabled, onChange }: Props) {
    const latest = useRef({ checked, disabled, onChange });
    latest.current = { checked, disabled, onChange };

    // A native listener: React's delegated handlers run after the row has
    // already seen the click.
    const area = useCallback((node: HTMLDivElement | null) => {
        if (!node) return;
        const onClick = (event: MouseEvent) => {
            event.stopPropagation();
            const { checked: current, disabled: off, onChange: change } = latest.current;
            if (off || (event.target as Element).closest('s-checkbox')) return;
            change(!current);
        };
        node.addEventListener('click', onClick);
        return () => node.removeEventListener('click', onClick);
    }, []);

    return (
        <div ref={area} className="pf-row-checkbox">
            <s-checkbox
                id={id}
                accessibilityLabel={accessibilityLabel}
                checked={checked}
                indeterminate={indeterminate}
                disabled={disabled}
                onChange={(event) => onChange(fieldChecked(event))}
            />
        </div>
    );
}
